import {
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    switchMap,
    takeUntil,
} from "rxjs/operators"
import { Observable, Subject } from "rxjs"
import {
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewChild,
} from "@angular/core"
import { NgForm } from "@angular/forms"
import { Router } from "@angular/router"
import { SearchingService } from "../../services/searching.service"
import { GTMService } from "../../services/gtm.service"
import { AddOnViewService } from "../../services/add-on-view.service"
import { UserService } from "../../services/user.service"
import { StudentGateService } from "../../services/student-gate.service"
import { BEFORE_SEARCH_PAGE_KEY } from "../../models/constants"
import {
    SearchSuggestion,
    SearchSuggestionsResponse,
    SearchDocument,
} from "../../models/search-suggestions.model"
import Utils from "../../utils/utils"
import { FeatureFlagsService } from "../../services/feature-flags.service"

type FlatSuggestionItem =
    | { type: "suggestion"; id: string; payload: SearchSuggestion }
    | { type: "document"; id: string; payload: SearchDocument }
    | { type: "viewAllResults"; id: string }

@Component({
    selector: "app-redesign-external-search-input",
    templateUrl: "./redesign-external-search-input.component.html",
    styleUrls: ["./redesign-external-search-input.component.scss"],
    standalone: false,
})
export class RedesignExternalSearchInputComponent implements OnInit, OnChanges, OnDestroy {
    private static nextInstanceId = 0
    public readonly instanceId = `redesign-search-${RedesignExternalSearchInputComponent.nextInstanceId++}`

    @ViewChild("formObj", { static: true }) public form: NgForm
    @ViewChild("formElRef", { static: true }) public formElRef: ElementRef<HTMLElement>
    @ViewChild("submitBtn") public submitBtn: ElementRef<HTMLElement>
    @ViewChild("formInput") public formInput: ElementRef<HTMLElement>
    @ViewChild("suggestionsDropdownRef", { read: ElementRef })
    public suggestionsDropdownRef: ElementRef<HTMLElement>

    @Input() placeholder: string = ""
    @Input() buttonText: string = ""
    @Input() type: string = "for-browse"
    @Input() isStudentExperience: boolean = false

    popularSearches: string[] = []
    isPopularOpen: boolean = false
    activePopularIndex: number = -1
    showPopularHeading: boolean = true

    suggestions: SearchSuggestionsResponse = { suggestions: [], documents: [] }
    isSuggestionsOpen: boolean = false
    activeSuggestionIndex: number = -1

    private searchTerm$ = new Subject<string>()
    private cancelPendingSuggestions$ = new Subject<void>()
    private destroy$ = new Subject<void>()
    private suppressNextInputChange: boolean = false

    constructor(
        public router: Router,
        public gtmService: GTMService,
        public featureFlagService: FeatureFlagsService,
        private searchingService: SearchingService,
        private addOnService: AddOnViewService,
        private userService: UserService,
        private gradeGate: StudentGateService
    ) {}

    public ngOnInit() {
        this.form.resetForm()

        if (this.featureFlagService.redesignSearchAutocompleteFeatureFlag) {
            this.searchTerm$
                .pipe(
                    debounceTime(300),
                    distinctUntilChanged(),
                    filter((term): term is string => !!term && term.trim().length >= 2),
                    switchMap(
                        (term: string): Observable<SearchSuggestionsResponse> =>
                            this.searchingService
                                .getSuggestionSearchTerms(term)
                                .pipe(takeUntil(this.cancelPendingSuggestions$))
                    ),
                    takeUntil(this.destroy$)
                )
                .subscribe((response: SearchSuggestionsResponse) => {
                    const currentTerm = this.form.value?.q ?? ""
                    if (!this.isAutocompleteEnabled || currentTerm.trim().length < 2) {
                        return
                    }

                    this.suggestions = response
                    this.isSuggestionsOpen =
                        response.suggestions.length > 0 || response.documents.length > 0
                })
        }
    }

    /**
     * The header persists across route changes and toggles isStudentExperience,
     * so clear any open or pending suggestions when entering the student experience.
     */
    public ngOnChanges(changes: SimpleChanges) {
        if (changes.isStudentExperience?.currentValue) {
            this.closeSuggestions()
        }
    }

    public ngOnDestroy() {
        this.cancelPendingSuggestions$.next()
        this.cancelPendingSuggestions$.complete()
        this.destroy$.next()
        this.destroy$.complete()
    }

    public get isAutocompleteEnabled(): boolean {
        return (
            this.featureFlagService.redesignSearchAutocompleteFeatureFlag &&
            !this.isStudentExperience
        )
    }

    public get activeDescendantId(): string | null {
        if (this.isPopularOpen && this.activePopularIndex >= 0) {
            return `${this.instanceId}-popular-option-${this.activePopularIndex}`
        }
        if (this.isSuggestionsOpen && this.activeSuggestionIndex >= 0) {
            const items = this.getFlattenedItems()
            return items[this.activeSuggestionIndex]?.id ?? null
        }
        return null
    }

    public get isAnyMenuOpen(): boolean {
        return this.isPopularOpen || this.isSuggestionsOpen
    }

    private getFlattenedItems(): FlatSuggestionItem[] {
        const items: FlatSuggestionItem[] = this.suggestions.suggestions.map((suggestion, i) => ({
            type: "suggestion" as const,
            id: `${this.instanceId}-suggestion-option-${i}`,
            payload: suggestion,
        }))

        let docIndex = 0
        for (const group of this.suggestions.documents) {
            for (const document of group.documents) {
                items.push({
                    type: "document" as const,
                    id: `${this.instanceId}-document-option-${docIndex}`,
                    payload: document,
                })
                docIndex++
            }
        }

        if (this.suggestions.documents.length > 0) {
            items.push({
                type: "viewAllResults" as const,
                id: `${this.instanceId}-view-all-option`,
            })
        }

        return items
    }

    public onSearch(form: NgForm) {
        const term = !!form.value?.q?.length ? form.value.q : ""
        this.performSearch(term, "external-search")
        this.closeSuggestions()
    }

    public onInputBlur(event: FocusEvent) {
        const nextFocus = event.relatedTarget as Node
        const isFocusMovingIntoDropdown =
            !!nextFocus &&
            !!this.suggestionsDropdownRef &&
            this.suggestionsDropdownRef.nativeElement.contains(nextFocus)

        if (isFocusMovingIntoDropdown) return

        this.handleFocusLeavingSuggestions(nextFocus)
    }

    public onSuggestionsDropdownBlur(event: FocusEvent) {
        this.handleFocusLeavingSuggestions(event.relatedTarget as Node)
    }

    private handleFocusLeavingSuggestions(nextFocus: Node) {
        const isFocusStillInForm = !!nextFocus && this.formElRef.nativeElement.contains(nextFocus)

        this.closeSuggestions()

        if (isFocusStillInForm) return

        const term = this.form.value?.q
        if (!term) return

        this.performSearch(term, "external-search")
    }

    public onSubmitButtonBlur(event: FocusEvent) {
        const nextFocus = event.relatedTarget as Node
        if (nextFocus && this.formElRef.nativeElement.contains(nextFocus)) return

        this.suppressNextInputChange = true
        this.form.resetForm()
    }

    public onFocusOrClick(term: string) {
        if (term && term.trim().length >= 2) {
            return
        }

        if (term) {
            this.closeSuggestions()
            return
        }

        this.loadPopularSuggestions()
    }

    public onInputChange(term: string) {
        if (this.suppressNextInputChange) {
            this.suppressNextInputChange = false
            return
        }

        if (!term || term.trim().length === 0) {
            this.closeSuggestions()
            this.loadPopularSuggestions()
            return
        }

        if (term.trim().length < 2) {
            this.closeSuggestions()
            return
        }

        this.cancelPendingSuggestions$.next()
        this.isPopularOpen = false
        this.popularSearches = []
        this.activeSuggestionIndex = -1
        if (this.isAutocompleteEnabled) {
            this.searchTerm$.next(term)
        }
    }

    public onInputKeydown(event: KeyboardEvent) {
        if (this.isPopularOpen) {
            this.handlePopularKeydown(event)
            return
        }

        if (this.isSuggestionsOpen) {
            this.handleSuggestionsKeydown(event)
        }
    }

    private handlePopularKeydown(event: KeyboardEvent) {
        const items = this.popularSearches
        if (!items.length) return

        switch (event.key) {
            case "ArrowDown":
                event.preventDefault()
                this.activePopularIndex = (this.activePopularIndex + 1) % items.length
                break
            case "ArrowUp":
                event.preventDefault()
                this.activePopularIndex =
                    this.activePopularIndex <= 0 ? items.length - 1 : this.activePopularIndex - 1
                break
            case "Enter":
                if (this.activePopularIndex >= 0) {
                    event.preventDefault()
                    this.onSelectPopularSearch(items[this.activePopularIndex])
                }
                break
            case "Escape":
                event.preventDefault()
                this.closeSuggestions()
                break
        }
    }

    private handleSuggestionsKeydown(event: KeyboardEvent) {
        const items = this.getFlattenedItems()
        if (!items.length) return

        switch (event.key) {
            case "ArrowDown":
                event.preventDefault()
                this.activeSuggestionIndex = (this.activeSuggestionIndex + 1) % items.length
                break
            case "ArrowUp":
                event.preventDefault()
                this.activeSuggestionIndex =
                    this.activeSuggestionIndex <= 0
                        ? items.length - 1
                        : this.activeSuggestionIndex - 1
                break
            case "Enter":
                if (this.activeSuggestionIndex >= 0) {
                    event.preventDefault()
                    const activeItem = items[this.activeSuggestionIndex]
                    if (activeItem.type === "suggestion") {
                        this.onSelectSearchSuggestion(activeItem.payload.text)
                    } else if (activeItem.type === "document") {
                        this.onSelectDocument(activeItem.payload)
                    } else {
                        this.onViewAllResults(this.form.value?.q)
                    }
                }
                break
            case "Escape":
                event.preventDefault()
                this.closeSuggestions()
                break
        }
    }

    public onSelectPopularSearch(suggestion: string) {
        this.performSearch(suggestion, "search-autocomplete")
        this.closeSuggestions()
    }

    public onSelectSearchSuggestion(suggestion: string) {
        this.performSearch(suggestion, "search-suggestion-term")
        this.closeSuggestions()
    }

    public onSelectDocument(document: SearchDocument) {
        this.storeCurrentUrlBeforeSearch()
        this.router.navigateByUrl(document.url).then(() => {
            this.suppressNextInputChange = true
            this.form.resetForm()
            this.formInput.nativeElement.blur()
            this.submitBtn.nativeElement.blur()
        })
        this.gtmService.pushEvent("search-suggestion-document")
        this.closeSuggestions()
    }

    public closeSuggestions() {
        this.cancelPendingSuggestions$.next()

        this.popularSearches = []
        this.isPopularOpen = false
        this.activePopularIndex = -1

        this.suggestions = { suggestions: [], documents: [] }
        this.isSuggestionsOpen = false
        this.activeSuggestionIndex = -1
    }

    private performSearch(term: string, gtmEvent: string) {
        const queryParams: any = { q: term }
        if (this.userService.hasStudentExperience()) {
            queryParams.student = true
            if (this.gradeGate.selectedGradeValue) {
                queryParams.selected_facet = "grades:" + this.gradeGate.selectedGradeValue
            }
        }

        this.storeCurrentUrlBeforeSearch()

        this.router
            .navigate([this.addOnService.isAddonView ? "add-on/search" : "search"], {
                queryParams,
            })
            .then((_) => {
                this.suppressNextInputChange = true
                this.form.resetForm()
                this.formInput.nativeElement.blur()
                this.submitBtn.nativeElement.blur()
            })

        this.gtmService.pushEvent(gtmEvent)
    }

    private loadPopularSuggestions() {
        this.searchingService
            .getPopularSearchTerms()
            .pipe(
                map((terms: string[]): string[] => {
                    if (!terms?.length) {
                        return []
                    }
                    const count = Math.min(5, terms.length)
                    return Utils.getRandomItems(terms, count)
                }),
                takeUntil(this.cancelPendingSuggestions$),
                takeUntil(this.destroy$)
            )
            .subscribe((popularSearches) => {
                this.popularSearches = popularSearches
                this.isPopularOpen = popularSearches.length > 0
                this.activePopularIndex = -1
            })
    }

    private storeCurrentUrlBeforeSearch(): void {
        localStorage.setItem(BEFORE_SEARCH_PAGE_KEY, window.location.href)
    }

    public onViewAllResults(term: string) {
        this.performSearch(term, "search-view-all-results")
        this.closeSuggestions()
    }
}
